import { useState } from "react";
import CnpjUtils from "../../../shared/utils/CnpjUtils";
import { throwResponseError } from "../../utils/api-error-utils";
import { getAuthParams } from "../../utils/pacto-rebates-utils";

const formatParticipantClient = client => {
    const id = client?.id;
    const name = client?.nome || "";
    const document = client?.documento || "";
    const groupName = client?.nomeGrupo || "";

    return {
        ...client,
        id,
        name,
        document,
        groupName,
        value: document,
        label: document ? `${name} - ${document}` : name,
        raw: client
    };
};

const formatParticipantClientsResponse = (clientsData, pagination = {}) => {
    const data = clientsData || {};
    const participantClientsData = data.resultado || [];

    const formattedParticipantClients = Array.isArray(participantClientsData)
        ? participantClientsData.map(formatParticipantClient)
        : [];

    return {
        items: formattedParticipantClients,
        totalElements: data.totalElementos ?? 0,
        totalPages: data.totalPaginas ?? 0,
        page: data.pagina ?? 0,
        size: data.tamanho ?? pagination.size ?? 10,
        raw: data
    };
};

export function useDashboardParticipantClients() {
    const [participantClients, setParticipantClients] = useState([]);
    const [pagination, setPagination] = useState({
        totalElements: 0,
        totalPages: 0,
        page: 0,
        size: 10
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchParticipantClients = async ({
        page = 0,
        size = 10,
        document = "",
        name = ""
    } = {}) => {
        try {
            setLoading(true);
            setError(null);

            const { accessToken, clientId } = getAuthParams();

            const params = new URLSearchParams();

            params.append("page", page);
            params.append("size", size);

            const cleanDocument = CnpjUtils.cleanAlphanumericCnpj(document || "");

            if (cleanDocument) {
                params.append("documento", cleanDocument);
            }

            if (name) {
                params.append("nome", name);
            }

            const response = await fetch(
                process.env.REACT_APP_ESSILOR_API +
                `pacto-rebates-dashboard/v1/dashboard/clientes?${params.toString()}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "access_token": accessToken,
                        "client_id": clientId
                    }
                }
            );

            if (!response.ok) {
                await throwResponseError(
                    response,
                    "Não foi possível carregar os clientes participantes."
                );
            }

            const data = await response.json();

            const formattedClients = formatParticipantClientsResponse(data, {
                page,
                size
            });

            const nextPagination = {
                totalElements: formattedClients.totalElements,
                totalPages: formattedClients.totalPages,
                page: formattedClients.page,
                size: formattedClients.size
            };

            setParticipantClients(formattedClients.items);
            setPagination(nextPagination);
            setError(null);

            return {
                ok: true,
                empty: formattedClients.items.length === 0,
                participantClients: formattedClients.items,
                pagination: nextPagination
            };
        } catch (requestError) {
            const errorMessage =
                requestError.message ||
                "Não foi possível carregar os clientes participantes.";

            setParticipantClients([]);
            setError(errorMessage);

            return {
                ok: false,
                reason: "request",
                error: errorMessage
            };
        } finally {
            setLoading(false);
        }
    };

    const clearParticipantClients = () => {
        setParticipantClients([]);
        setPagination({
            totalElements: 0,
            totalPages: 0,
            page: 0,
            size: 10
        });
        setError(null);
    };

    return {
        participantClients,
        participantClientsPagination: pagination,
        participantClientsLoading: loading,
        participantClientsError: error,
        fetchParticipantClients,
        clearParticipantClients
    };
}